import CommHandler from './CommHandler';
import { getAuthHeader } from './utils';
import { broadcastNotificationUpdate } from './notificationUtils';

/**
 * Send a notification to a user or users.
 * @param {string} fromUserId - ID of the sender
 * @param {string|string[]} toUserId - User ID(s) or email(s) of the recipient(s)
 * @param {string} message - Notification message
 * @param {string} [title] - Optional notification title
 * @param {number} quarter - Quarter (1-4)
 * @param {string} financialYear - Financial year in format YYYY-YYYY
 * @param {string} [department] - Department name (PROJECT, BU, HR, RM, Company)
 * @param {string} [comment] - Comment for challenge notifications
 * @returns {Promise<any>} API response
 */
export const sendNotification = async ({
  fromUserId,
  toUserId,
  message,
  title,
  quarter,
  financialYear,
  department,
  comment,
}: {
  fromUserId: any;
  toUserId: any;
  message: any;
  title?: any;
  quarter: any;
  financialYear: any;
  department?: any;
  comment?: any;
}) => {
  const response = await CommHandler.request({
    url: '/notification/send',
    method: 'POST',
    data: {
      user_id: fromUserId,
      send_to: toUserId,
      message,
      quarter,
      financialYear,
      department,
      title,
      ...(comment ? { comment } : {}),
    },
  });

  broadcastNotificationUpdate(toUserId);
  return response;
};

/**
 * Get unread notifications for the current user.
 * @param {string} userId - ID of the user
 * @returns {Promise<any>} API response with unread notifications
 */
export const getUnreadNotifications = async (userId: string) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/unread',
      method: 'GET',
      params: { user_id: userId },
      headers: getAuthHeader,
    });
    return response?.data || [];
  } catch (error) {
    console.error('Error fetching unread notifications:', error);
    return [];
  }
};

/**
 * Get notification count for the current user.
 * @param {string} userId - ID of the user
 * @returns {Promise<number>} Notification count
 */
export const getNotificationCount = async (userId: string) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/count',
      method: 'GET',
      params: { user_id: userId },
      headers: getAuthHeader,
    });
    return response?.data?.count || 0;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    return 0;
  }
};

/**
 * Mark notifications as read.
 * @param {number[]} notificationIds - Array of notification IDs to mark as read
 * @returns {Promise<any>} API response
 */
export const markNotificationsAsRead = async (notificationIds: number[]) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/mark-as-read',
      method: 'PUT',
      data: { notification_ids: notificationIds },
      headers: getAuthHeader,
    });
    return response;
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
};
